import { type AppDescription, type TabDescription, getScreenProperty } from "@glide/app-description";
import { getAppTabs } from "@glide/common-core/dist/js/components/SerializedApp";
import { defined } from "@glideapps/ts-necessities";
import { logError, replaceArrayItem, truthify } from "@glide/support";
import { isChatTab, isShoppingCartTab } from "@glide/generator/dist/js/screens";

export function updateTabDescriptionForAppDescription(
    app: AppDescription,
    index: number,
    tabDescription: TabDescription
): AppDescription {
    const oldTabs = getAppTabs(app);
    const currentTab = oldTabs[index];

    if (currentTab === undefined) {
        logError(`Invalid tab index ${index} for update. Tab count: ${oldTabs.length}`);
        return app;
    }

    if (tabDescription.propertyName !== currentTab.propertyName) {
        logError(
            `Mismatch for tab at index ${index}. Expected: ${currentTab.propertyName}, got: ${tabDescription.propertyName}`
        );
        return app;
    }

    const newTabs = replaceArrayItem(oldTabs, index, tabDescription);
    const screenName = getScreenProperty(tabDescription.screenName);
    if (screenName === undefined) return app;
    return {
        ...app,
        tabs: newTabs,
    };
}

export function removeTab(app: AppDescription, screenName: string): AppDescription {
    const index = getAppTabs(app).findIndex(t => getScreenProperty(t.screenName) === screenName);
    if (index < 0) return app;

    const oldTabs = getAppTabs(app);
    const toRemove = defined(oldTabs[index]);

    if (isShoppingCartTab(toRemove) || isChatTab(toRemove)) {
        logError("Removing special tabs is not allowed.");
        return app;
    }

    const newTabs = [...oldTabs.slice(0, index), ...oldTabs.slice(index + 1)];

    // We have to treat Flyout tabs as "hidden" for the purposes
    // of fixing the navigation model. If we don't, then deleting
    // a Tab Bar tab could replace it with a Flyout tab that removes
    // the Tab Bar and makes those tabs permanently inaccessible
    // (until page refresh).
    let visibleIndexes = newTabs.map((t, i) => (t.hidden || truthify(t.inFlyout) ? -1 : i)).filter(i => i >= 0);

    // It is technically possible to remove the singular non-flyout tab.
    // When this happens, we need to bump the first flyout tab out.
    if (visibleIndexes.length === 0 && newTabs.length > 0) {
        let firstTabIndex = newTabs.findIndex(t => !t.hidden);
        if (firstTabIndex < 0) {
            // If we don't even have a tab in the flyout, all tabs are
            // hidden, so we unhide the first tab.
            firstTabIndex = 0;
        }
        newTabs[firstTabIndex] = {
            ...defined(newTabs[firstTabIndex]),
            inFlyout: false,
            hidden: false,
        };
        visibleIndexes = [firstTabIndex];
    }

    return { ...app, tabs: newTabs };
}
